import React, { useRef, useState } from "react";
import { DatePicker, PickersDay } from "@mui/x-date-pickers";
import { Controller, useController } from "react-hook-form";
import TextFieldMUI from "@mui/material/TextField";
import i18next from "i18next";
import Typography from "@mui/material/Typography";
import {
  DATE_LONG_TEXT,
  FIELD_REQUIRED,
  BUTTON_START_NOW,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import _ from "lodash";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import Button from "../Buttons/Button";

const DateField = React.forwardRef(function DateField(
  {
    control,
    rules = {
      required: {
        value: true,
        message: i18next.t(FIELD_REQUIRED),
      },
    },
    label,
    minDate,
    maxDate,
    errors = {},
    name = "",
    disabled = false,
    actionsChange = () => {},
    onOpen = () => {},
    value = null,
    variant = "filled",
    mode = "input",
    sx = {},
    isDisabledClickText = false,
    onClickText = () => {},
    formatText = DATE_LONG_TEXT,
    propsText = {},
    className = "",
    fullWidth = true,
    clearable = false,
    defaultCalendarMonth = null,
    renderDay = null,
    showStartNowButton = false,
  },
  ref
) {
  const [date, setDate] = useState(value);
  const refIcon = useRef(null);

  const error = getReactHookFormNestedError(errors, name);

  const { t } = useTranslationApp();

  const sxMode =
    mode === "text"
      ? {
          visibility: "hidden",
          position: "absolute",
        }
      : {};

  const {
    field: { onChange: onChangeField },
  } = useController({
    control,
    name,
    rules,
  });

  const computedRenderDay = (day, _value, DayComponentProps) => {
    if (renderDay) return renderDay(day, _value, DayComponentProps);
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    let isDisabled = false;
    if (minDate) {
      const minDay = new Date(minDate);
      minDay.setHours(0, 0, 0, 0);
      if (dayStart < minDay) isDisabled = true;
    }
    if (maxDate) {
      const maxDay = new Date(maxDate);
      maxDay.setHours(0, 0, 0, 0);
      if (dayStart > maxDay) isDisabled = true;
    }
    return (
      <PickersDay
        {...DayComponentProps}
        disabled={isDisabled}
        sx={{ opacity: isDisabled ? 0.35 : 1 }}
      />
    );
  };

  const onClickClear = () => {
    onChangeField(null);
    setDate(null);
    actionsChange(null);
  };

  const onClickStartNow = () => {
    const now = new Date();
    onChangeField(now);
    setDate(now);
    actionsChange(now);
  };

  const isNowValidForStartNow = () => {
    const now = new Date();

    if (minDate && now < minDate) {
      return false;
    }
    if (maxDate && now > maxDate) {
      return false;
    }

    return true;
  };

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      {mode === "text" && (
        <Typography
          variant="h6"
          {...propsText}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              cursor: "pointer",
              textDecoration: "underline",
            },
            ...(propsText?.sx || {}),
          }}
          onClick={() => {
            if (isDisabledClickText) return;
            const child = refIcon.current.children[1];
            if (child) {
              child.click();
              onClickText();
            }
          }}
        >
          {t(formatText, {
            date: date,
          })}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Controller
          name={name}
          defaultValue={value}
          control={control}
          rules={rules}
          render={({ field }) => (
            <DatePicker
              {...field}
              ref={ref}
              PopperProps={{
                placement: "bottom-start",
                modifiers: [
                  {
                    name: "flip",
                    enabled: true,
                    options: {
                      fallbackPlacements: [
                        "top-start",
                        "top-end",
                        "bottom-end",
                      ],
                    },
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      boundary: "viewport",
                      altAxis: true,
                      padding: 8,
                    },
                  },
                  {
                    name: "offset",
                    enabled: true,
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
              }}
              defaultCalendarMonth={defaultCalendarMonth}
              minDate={minDate || undefined}
              maxDate={maxDate || undefined}
              onOpen={onOpen}
              onChange={(date) => {
                const isValueDate = _.isDate(date) && !isNaN(date.getTime());
                field.onChange(isValueDate ? date : null);
                setDate(isValueDate ? date : null);
                actionsChange(isValueDate ? date : null);
              }}
              label={label}
              disabled={disabled}
              renderDay={computedRenderDay}
              InputAdornmentProps={{
                position: "start",
                ref: refIcon,
              }}
              {...(showStartNowButton &&
                isNowValidForStartNow() && {
                  components: {
                    ActionBar: ({ onAccept }) => (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          pb: 2,
                          px: 2,
                        }}
                      >
                        <Button
                          onClick={() => {
                            onClickStartNow();
                            onAccept();
                          }}
                          size="small"
                          color="secondary"
                        >
                          {t(BUTTON_START_NOW)}
                        </Button>
                      </Box>
                    ),
                  },
                })}
              renderInput={(params) => (
                <TextFieldMUI
                  {...params}
                  name={name}
                  className={className}
                  fullWidth={fullWidth}
                  disabled={disabled}
                  variant={variant}
                  error={!!error}
                  hiddenLabel={!label}
                  sx={{
                    ...sx,
                    ...sxMode,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                  }}
                  onClick={() => {
                    if (disabled) return;
                    const child = refIcon.current?.children?.[1];
                    if (child) {
                      child?.click();
                    }
                  }}
                />
              )}
            />
          )}
        />
        {error?.message && (
          <Typography variant="caption" color="error">
            {error?.message}
          </Typography>
        )}
      </Box>
      {clearable && !disabled && (
        <IconButton
          onClick={onClickClear}
          sx={{
            position: "absolute",
            right: 12,
            top: 16,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
});

export default DateField;
